/**
 * AI Menu Import - KI-basierte Speisekarten-Extraktion
 * 
 * Features:
 * - PDF/Bild-Upload
 * - KI-Extraktion anzeigen
 * - Verifizierung und Bearbeitung
 * - Import in Datenbank
 * 
 * Design: Glassmorphism + Bento Cards + Teal/Emerald Akzente
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload, FileText, CheckCircle, AlertCircle, Edit2, Trash2,
  ChevronDown, ChevronRight, Save, ArrowLeft, Sparkles, RefreshCw,
  Plus, X, DollarSign, UtensilsCrossed, MessageSquare, Wand2,
  FileImage, Zap, Brain, Target, Settings2
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Alert,
  AlertDescription,
} from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import ProductSetupWizard from "@/components/ProductSetupWizard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Upload States
const UPLOAD_STATES = {
  IDLE: 'idle',
  UPLOADING: 'uploading',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  ERROR: 'error'
};

// Custom CSS for shimmer effect
const shimmerStyles = `
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
  .shimmer-effect::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.04), transparent);
    animation: shimmer 2.5s infinite;
  }
  @keyframes pulse-glow {
    0%, 100% { box-shadow: 0 0 20px rgba(47, 128, 255, 0.15); }
    50% { box-shadow: 0 0 40px rgba(47, 128, 255, 0.25); }
  }
  .pulse-glow {
    animation: pulse-glow 2s ease-in-out infinite;
  }
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-6px); }
  }
  .float-animation {
    animation: float 3s ease-in-out infinite;
  }
`;

export default function AIMenuImport() {
  const navigate = useNavigate();
  const [sessionToken] = useState(() => localStorage.getItem('access_token'));
  
  // Upload state
  const [uploadState, setUploadState] = useState(UPLOAD_STATES.IDLE);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  
  // Extraction result
  const [extractionResult, setExtractionResult] = useState(null);
  const [editedCategories, setEditedCategories] = useState([]);
  
  // Edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCategoryIndex, setEditingCategoryIndex] = useState(null);
  const [editingProductIndex, setEditingProductIndex] = useState(null);
  
  // Import state
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Wizard state
  const [showWizard, setShowWizard] = useState(false);

  // Handle file drop
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  // Upload and extract
  const handleFileUpload = async (file) => {
    // Validate file
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Nicht unterstütztes Format. Erlaubt: PDF, JPG, PNG, WEBP');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Datei zu groß (max 10MB)');
      return;
    }
    
    setUploadState(UPLOAD_STATES.UPLOADING);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 500);
      
      setUploadState(UPLOAD_STATES.PROCESSING);
      
      const response = await fetch(`${BACKEND_URL}/api/menu-ai/extract`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        },
        body: formData
      });
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Extraktion fehlgeschlagen');
      }
      
      const result = await response.json();
      setExtractionResult(result);
      setEditedCategories(JSON.parse(JSON.stringify(result.categories))); // Deep copy for editing
      setUploadState(UPLOAD_STATES.COMPLETED);
      
      // Automatisch den Wizard starten nach erfolgreicher Extraktion
      setShowWizard(true);
      
      toast.success(`${result.statistics?.total_products || 0} Produkte erkannt! KI-Assistent wird gestartet...`);
      
    } catch (error) {
      console.error('Upload error:', error);
      setUploadState(UPLOAD_STATES.ERROR);
      toast.error(error.message || 'Fehler bei der Extraktion');
    }
  };

  // Edit product
  const openEditModal = (categoryIndex, productIndex) => {
    const product = editedCategories[categoryIndex].products[productIndex];
    setEditingProduct(JSON.parse(JSON.stringify(product))); // Deep copy
    setEditingCategoryIndex(categoryIndex);
    setEditingProductIndex(productIndex);
    setEditModalOpen(true);
  };

  const saveProductEdit = () => {
    if (editingProduct && editingCategoryIndex !== null && editingProductIndex !== null) {
      const newCategories = [...editedCategories];
      newCategories[editingCategoryIndex].products[editingProductIndex] = editingProduct;
      setEditedCategories(newCategories);
      setEditModalOpen(false);
      toast.success('Produkt aktualisiert');
    }
  };

  // Delete product
  const deleteProduct = (categoryIndex, productIndex) => {
    const newCategories = [...editedCategories];
    newCategories[categoryIndex].products.splice(productIndex, 1);
    
    // Remove category if empty
    if (newCategories[categoryIndex].products.length === 0) {
      newCategories.splice(categoryIndex, 1);
    }
    
    setEditedCategories(newCategories);
    toast.success('Produkt entfernt');
  };

  // Import products
  const handleImport = async () => {
    if (!extractionResult?.extraction_id) {
      toast.error('Keine Extraktion vorhanden');
      return;
    }
    
    setImporting(true);
    
    try {
      // Flatten products for import
      const products = [];
      editedCategories.forEach(category => {
        category.products.forEach(product => {
          products.push({
            name: product.name,
            description: product.description,
            category_name: category.name,
            base_price: product.base_price,
            variants: product.variants || [],
            ingredients: product.ingredients || [],
            staff_prompt: product.staff_prompt
          });
        });
      });
      
      const response = await fetch(`${BACKEND_URL}/api/menu-ai/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify({
          extraction_id: extractionResult.extraction_id,
          products: products,
          create_categories: true
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Import fehlgeschlagen');
      }
      
      const result = await response.json();
      setImportResult(result);
      
      toast.success(`${result.imported} Produkte importiert!`);
      
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.message || 'Import fehlgeschlagen');
    } finally {
      setImporting(false);
    }
  };

  // Reset to start over
  const handleReset = () => {
    setUploadState(UPLOAD_STATES.IDLE);
    setUploadProgress(0);
    setExtractionResult(null);
    setEditedCategories([]);
    setImportResult(null);
    setShowWizard(false);
  };

  // Handle wizard completion
  const handleWizardComplete = (updatedCategories) => {
    setEditedCategories(updatedCategories);
    setShowWizard(false);
    toast.success('Produkte wurden konfiguriert! Sie können jetzt importieren.');
  };

  // Calculate totals
  const totalProducts = editedCategories.reduce(
    (sum, cat) => sum + cat.products.length, 0
  );

  return (
    <div className="min-h-screen bg-[#0B1220] relative overflow-hidden">
      {/* Inject shimmer styles */}
      <style>{shimmerStyles}</style>
      
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#2F80FF]/5 rounded-full blur-3xl" />
        <div className="absolute top-1/3 -left-32 w-72 h-72 bg-[#2DD4BF]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-[#22C55E]/5 rounded-full blur-3xl" />
      </div>
      
      {/* Header - Glassmorphism */}
      <header className="relative bg-[#111C32]/80 border-b border-slate-700/50 backdrop-blur-xl supports-[backdrop-filter]:bg-[#111C32]/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                data-testid="back-button"
                variant="ghost"
                size="sm"
                onClick={() => navigate('/app/produkte')}
                className="text-slate-400 hover:text-white hover:bg-slate-800/50 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück
              </Button>
              <div className="h-6 w-px bg-slate-700" />
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  <div className="relative">
                    <Wand2 className="w-5 h-5 text-[#2DD4BF]" />
                    <div className="absolute inset-0 animate-ping">
                      <Wand2 className="w-5 h-5 text-[#2DD4BF] opacity-30" />
                    </div>
                  </div>
                  KI-Menü-Import
                </h1>
                <p className="text-sm text-slate-400">
                  Speisekarte hochladen und automatisch importieren
                </p>
              </div>
            </div>
            
            {uploadState === UPLOAD_STATES.COMPLETED && !importResult && (
              <Button
                data-testid="import-products-button"
                onClick={handleImport}
                disabled={importing || totalProducts === 0}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-lg shadow-emerald-500/20 transition-all hover:shadow-emerald-500/30"
              >
                {importing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Importiere...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {totalProducts} Produkte importieren
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </header>
      
      <main className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Upload Area */}
        {uploadState === UPLOAD_STATES.IDLE && (
          <div className="space-y-8">
            {/* Main Upload Card */}
            <Card className="relative bg-[#111C32]/90 border-slate-700/60 backdrop-blur-xl overflow-hidden">
              {/* Decorative gradient overlay */}
              <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2F80FF]/5 to-transparent pointer-events-none" />
              
              <CardHeader className="text-center pb-4 relative">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#2F80FF]/20 to-[#2DD4BF]/20 border border-[#2F80FF]/30 mx-auto mb-4 float-animation">
                  <Brain className="w-8 h-8 text-[#2DD4BF]" />
                </div>
                <CardTitle className="text-white text-2xl sm:text-3xl tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Speisekarte hochladen
                </CardTitle>
                <CardDescription className="text-slate-400 max-w-lg mx-auto text-base">
                  Laden Sie Ihre Speisekarte als PDF oder Bild hoch. Die KI extrahiert automatisch alle Kategorien, Produkte und Preise.
                </CardDescription>
              </CardHeader>
              
              <CardContent className="relative">
                {/* Upload Dropzone */}
                <div
                  data-testid="upload-dropzone"
                  role="button"
                  aria-label="Datei hochladen"
                  aria-dropeffect="copy"
                  tabIndex={0}
                  className={`
                    relative group rounded-2xl p-10 sm:p-14 text-center cursor-pointer
                    transition-all duration-300 overflow-hidden
                    ${dragActive 
                      ? 'border-2 border-[#2F80FF] bg-[#2F80FF]/10 ring-4 ring-[#2F80FF]/20 pulse-glow' 
                      : 'border-2 border-dashed border-slate-600/80 hover:border-[#2DD4BF]/60 bg-[#0E1628]/50 hover:bg-[#0E1628]/80'}
                  `}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('file-input').click()}
                  onKeyDown={(e) => e.key === 'Enter' && document.getElementById('file-input').click()}
                >
                  {/* Shimmer effect on hover */}
                  <div className="absolute inset-0 shimmer-effect opacity-0 group-hover:opacity-100 transition-opacity" />
                  
                  <input
                    type="file"
                    id="file-input"
                    data-testid="file-input"
                    className="hidden"
                    accept=".pdf,.jpg,.jpeg,.png,.webp"
                    onChange={handleFileSelect}
                  />
                  
                  <div className="relative">
                    <div className={`
                      inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-6
                      transition-all duration-300
                      ${dragActive 
                        ? 'bg-[#2F80FF]/20 scale-110' 
                        : 'bg-slate-800/60 group-hover:bg-[#2DD4BF]/10 group-hover:scale-105'}
                    `}>
                      <Upload className={`
                        w-10 h-10 transition-colors
                        ${dragActive ? 'text-[#2F80FF]' : 'text-slate-500 group-hover:text-[#2DD4BF]'}
                      `} />
                    </div>
                    
                    <p className="text-lg sm:text-xl text-slate-200 mb-2 font-medium">
                      {dragActive ? 'Datei hier ablegen' : 'Datei hierher ziehen oder klicken'}
                    </p>
                    <p className="text-sm text-slate-500">
                      PDF, JPG, PNG, WEBP • Max. 10MB
                    </p>
                    
                    {/* File type badges */}
                    <div className="flex items-center justify-center gap-2 mt-4">
                      {['PDF', 'JPG', 'PNG'].map((type) => (
                        <Badge 
                          key={type} 
                          variant="outline" 
                          className="border-slate-600/60 text-slate-400 bg-slate-800/40"
                        >
                          {type}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Feature Cards - Bento Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                {
                  icon: Zap,
                  iconColor: 'text-amber-400',
                  bgColor: 'from-amber-500/10 to-amber-500/5',
                  borderColor: 'border-amber-500/20',
                  title: 'Automatische Erkennung',
                  description: 'Kategorien, Produkte, Preise und Zutaten werden automatisch erkannt'
                },
                {
                  icon: Target,
                  iconColor: 'text-emerald-400',
                  bgColor: 'from-emerald-500/10 to-emerald-500/5',
                  borderColor: 'border-emerald-500/20',
                  title: 'Upselling-Vorschläge',
                  description: 'KI generiert automatisch passende Upselling-Empfehlungen'
                },
                {
                  icon: MessageSquare,
                  iconColor: 'text-[#2DD4BF]',
                  bgColor: 'from-[#2DD4BF]/10 to-[#2DD4BF]/5',
                  borderColor: 'border-[#2DD4BF]/20',
                  title: 'Bestellhilfe',
                  description: 'Texte für Mitarbeiter zur Bestellaufnahme werden erstellt'
                }
              ].map((feature, idx) => (
                <Card 
                  key={idx}
                  data-testid={`feature-card-${idx}`}
                  className={`
                    relative overflow-hidden bg-gradient-to-br ${feature.bgColor}
                    border ${feature.borderColor} backdrop-blur-sm
                    transition-all duration-300 hover:-translate-y-1 hover:shadow-lg
                  `}
                >
                  <CardContent className="p-5">
                    <div className={`
                      inline-flex items-center justify-center w-10 h-10 rounded-xl mb-3
                      bg-slate-800/60 border border-slate-700/50
                    `}>
                      <feature.icon className={`w-5 h-5 ${feature.iconColor}`} />
                    </div>
                    <h3 className="font-semibold text-white mb-1.5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      {feature.title}
                    </h3>
                    <p className="text-sm text-slate-400 leading-relaxed">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
        
        {/* Processing State */}
        {(uploadState === UPLOAD_STATES.UPLOADING || uploadState === UPLOAD_STATES.PROCESSING) && (
          <Card className="bg-[#111C32]/90 border-slate-700/60 backdrop-blur-xl">
            <CardContent className="py-16 sm:py-20 text-center">
              <div className="relative inline-block mb-8">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#2F80FF]/20 to-[#2DD4BF]/20 flex items-center justify-center">
                  <RefreshCw className="w-10 h-10 text-[#2DD4BF] animate-spin" />
                </div>
                <div className="absolute -inset-4 rounded-3xl bg-[#2F80FF]/10 animate-pulse" />
              </div>
              
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {uploadState === UPLOAD_STATES.UPLOADING ? 'Datei wird hochgeladen...' : 'KI analysiert Ihre Speisekarte...'}
              </h2>
              <p className="text-slate-400 mb-8 max-w-md mx-auto">
                {uploadState === UPLOAD_STATES.PROCESSING && 'Die KI erkennt Kategorien, Produkte und Preise. Dies kann bis zu 30 Sekunden dauern.'}
              </p>
              
              <div className="max-w-md mx-auto">
                <Progress 
                  data-testid="upload-progress" 
                  value={uploadProgress} 
                  className="h-2.5 bg-slate-800 [&>div]:bg-gradient-to-r [&>div]:from-[#2F80FF] [&>div]:via-[#2DD4BF] [&>div]:to-emerald-400 [&>div]:transition-all" 
                />
                <p data-testid="upload-progress-percent" className="text-sm text-slate-500 mt-3">{uploadProgress}%</p>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Error State */}
        {uploadState === UPLOAD_STATES.ERROR && (
          <Card className="bg-[#111C32]/90 border-red-800/50 backdrop-blur-xl">
            <CardContent className="py-14 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/30 mb-6">
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Fehler bei der Extraktion
              </h2>
              <p className="text-slate-400 mb-6 max-w-md mx-auto">
                Die Speisekarte konnte nicht verarbeitet werden. Bitte versuchen Sie es mit einem anderen Bild.
              </p>
              <Button 
                data-testid="reset-import-button"
                onClick={handleReset} 
                variant="outline" 
                className="border-slate-600 text-slate-300 hover:bg-slate-800/50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Erneut versuchen
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* Results */}
        {uploadState === UPLOAD_STATES.COMPLETED && !importResult && (
          <div className="space-y-6">
            
            {/* Summary Card */}
            <Card className="bg-[#111C32]/90 border-slate-700/60 backdrop-blur-xl overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 via-teal-500 to-[#2F80FF]" />
              <CardContent className="py-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center border border-emerald-500/30">
                      <CheckCircle className="w-7 h-7 text-emerald-400" />
                    </div>
                    <div>
                      <h2 data-testid="extraction-success-title" className="text-lg font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                        Extraktion erfolgreich!
                      </h2>
                      <p data-testid="total-products-text" className="text-slate-400">
                        {editedCategories.length} Kategorien, {totalProducts} Produkte erkannt
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className="border-emerald-500/50 text-emerald-400 bg-emerald-500/10">
                      {Math.round((extractionResult?.confidence_score || 0) * 100)}% Konfidenz
                    </Badge>
                    <Button 
                      data-testid="reset-import-button"
                      variant="ghost" 
                      size="sm"
                      onClick={handleReset}
                      className="text-slate-400 hover:text-white"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Abbrechen
                    </Button>
                  </div>
                </div>
                
                {extractionResult?.warnings?.length > 0 && (
                  <Alert className="mt-4 bg-amber-500/10 border-amber-500/30">
                    <AlertCircle className="w-4 h-4 text-amber-400" />
                    <AlertDescription className="text-amber-300">
                      {extractionResult.warnings.join(', ')}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
            
            {/* Product Setup Wizard - Automatisch nach Extraktion */}
            {showWizard && extractionResult && (
              <ProductSetupWizard
                extractionId={extractionResult.extraction_id}
                categories={editedCategories}
                onComplete={handleWizardComplete}
                onCancel={() => setShowWizard(false)}
              />
            )}
            
            {/* Wizard wieder öffnen Button - wenn geschlossen */}
            {!showWizard && (
              <Card className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border-purple-500/30 backdrop-blur-xl overflow-hidden">
                <CardContent className="py-4">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/30 to-pink-500/30 flex items-center justify-center border border-purple-500/40">
                        <MessageSquare className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-slate-300 text-sm">
                          Möchten Sie Änderungen vornehmen? Öffnen Sie den KI-Assistenten erneut.
                        </p>
                      </div>
                    </div>
                    <Button
                      data-testid="reopen-wizard-button"
                      onClick={() => setShowWizard(true)}
                      variant="outline"
                      className="border-purple-500/50 text-purple-300 hover:bg-purple-500/10"
                    >
                      <Wand2 className="w-4 h-4 mr-2" />
                      KI-Chat öffnen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Categories & Products - Bento Cards - nur wenn Wizard nicht aktiv */}
            {!showWizard && (
            <div className="space-y-5">
              {editedCategories.map((category, catIndex) => (
                <Card 
                  key={catIndex} 
                  data-testid={`category-card-${catIndex}`}
                  className="bg-[#111C32]/80 border-slate-700/60 backdrop-blur-md overflow-hidden"
                >
                  <CardHeader className="pb-3 border-b border-slate-700/40">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-white flex items-center gap-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                        <div className="w-2 h-8 rounded-full bg-gradient-to-b from-[#2DD4BF] to-emerald-500" />
                        {category.name}
                        <Badge className="ml-2 bg-slate-800 text-slate-300 border-slate-700">
                          {category.products.length} Produkte
                        </Badge>
                      </CardTitle>
                    </div>
                    {category.description && (
                      <CardDescription className="text-slate-400 ml-5">
                        {category.description}
                      </CardDescription>
                    )}
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {category.products.map((product, prodIndex) => (
                        <div 
                          key={prodIndex}
                          data-testid={`product-card-${catIndex}-${prodIndex}`}
                          className="
                            group relative bg-[#0E1628]/60 rounded-xl p-4
                            border border-slate-700/60 hover:border-slate-500/60
                            backdrop-blur-sm transition-all duration-200
                            hover:-translate-y-[1px] hover:shadow-lg hover:shadow-slate-900/20
                          "
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="font-medium text-white truncate">{product.name}</h4>
                                <span className="shrink-0 text-emerald-400 font-semibold px-2.5 py-0.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm">
                                  €{product.base_price?.toFixed(2)}
                                </span>
                              </div>
                              
                              {product.description && (
                                <p className="text-sm text-slate-400 mt-1.5 line-clamp-2">{product.description}</p>
                              )}
                              
                              {/* Variants */}
                              {product.variants?.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2.5">
                                  {product.variants.map((v, i) => (
                                    <Badge 
                                      key={i} 
                                      variant="outline" 
                                      className="text-xs border-slate-600/60 text-slate-300 bg-slate-800/40"
                                    >
                                      {v.name}: €{v.price?.toFixed(2)}
                                    </Badge>
                                  ))}
                                </div>
                              )}
                              
                              {/* Ingredients */}
                              {product.ingredients?.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2.5">
                                  {product.ingredients.slice(0, 4).map((ing, i) => (
                                    <Badge 
                                      key={i} 
                                      className="text-xs bg-slate-700/50 text-slate-300 border-0"
                                    >
                                      {ing.removable && <X className="w-2 h-2 mr-0.5" />}
                                      {ing.name}
                                    </Badge>
                                  ))}
                                  {product.ingredients.length > 4 && (
                                    <Badge className="text-xs bg-slate-700/50 text-slate-400 border-0">
                                      +{product.ingredients.length - 4}
                                    </Badge>
                                  )}
                                </div>
                              )}
                              
                              {/* Staff Prompt */}
                              {product.staff_prompt && (
                                <div className="mt-3 p-2.5 bg-[#2F80FF]/10 rounded-lg border border-[#2F80FF]/20">
                                  <p className="text-xs text-blue-300 flex items-start gap-1.5">
                                    <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                    <span>{product.staff_prompt}</span>
                                  </p>
                                </div>
                              )}
                              
                              {/* Upsell */}
                              {product.upsell_suggestions?.length > 0 && (
                                <div className="mt-2.5 p-2.5 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                                  <p className="text-xs text-emerald-300 flex items-start gap-1.5">
                                    <Target className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                    <span>{product.upsell_suggestions[0]}</span>
                                  </p>
                                </div>
                              )}
                            </div>
                            
                            <div className="flex gap-1 ml-3 opacity-60 group-hover:opacity-100 transition-opacity">
                              <Button
                                data-testid={`product-edit-button-${catIndex}-${prodIndex}`}
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditModal(catIndex, prodIndex)}
                                className="text-slate-400 hover:text-white hover:bg-slate-700/50 h-8 w-8 p-0"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                data-testid={`product-delete-button-${catIndex}-${prodIndex}`}
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteProduct(catIndex, prodIndex)}
                                className="text-slate-400 hover:text-red-400 hover:bg-red-500/10 h-8 w-8 p-0"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            )}
          </div>
        )}
        
        {/* Import Success */}
        {importResult && (
          <Card className="bg-[#111C32]/90 border-emerald-700/50 backdrop-blur-xl overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 to-teal-500" />
            <CardContent className="py-14 sm:py-16 text-center">
              <div className="relative inline-block mb-8">
                <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 flex items-center justify-center border border-emerald-500/30">
                  <CheckCircle className="w-12 h-12 text-emerald-400" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-sm font-bold">
                  ✓
                </div>
              </div>
              
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                Import erfolgreich!
              </h2>
              <p className="text-slate-400 mb-8 max-w-md mx-auto">
                {importResult.imported} Produkte wurden importiert
                {importResult.skipped > 0 && `, ${importResult.skipped} übersprungen (bereits vorhanden)`}
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-3">
                <Button
                  data-testid="go-to-products-button"
                  onClick={() => navigate('/app/produkte')}
                  className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-lg"
                >
                  <UtensilsCrossed className="w-4 h-4 mr-2" />
                  Zu den Produkten
                </Button>
                <Button
                  data-testid="import-another-button"
                  variant="outline"
                  onClick={handleReset}
                  className="border-slate-600 text-slate-300 hover:bg-slate-800/50"
                >
                  Weitere Speisekarte importieren
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
      </main>
      
      {/* Edit Modal - Glassmorphism */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-[#111C32]/95 border-slate-700/70 backdrop-blur-xl max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              <Edit2 className="w-5 h-5 text-[#2DD4BF]" />
              Produkt bearbeiten
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Passen Sie die extrahierten Daten an
            </DialogDescription>
          </DialogHeader>
          
          {editingProduct && (
            <div className="space-y-5 py-2">
              {/* Name & Price */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Name</Label>
                  <Input
                    data-testid="dialog-product-name"
                    value={editingProduct.name}
                    onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})}
                    className="bg-slate-800/80 border-slate-600/80 text-white focus-visible:ring-[#2F80FF]"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Grundpreis (€)</Label>
                  <Input
                    data-testid="dialog-product-price"
                    type="number"
                    step="0.01"
                    value={editingProduct.base_price}
                    onChange={(e) => setEditingProduct({...editingProduct, base_price: parseFloat(e.target.value) || 0})}
                    className="bg-slate-800/80 border-slate-600/80 text-white focus-visible:ring-[#2F80FF]"
                  />
                </div>
              </div>
              
              {/* Description */}
              <div className="space-y-2">
                <Label className="text-slate-300">Beschreibung</Label>
                <Textarea
                  data-testid="dialog-product-description"
                  value={editingProduct.description || ''}
                  onChange={(e) => setEditingProduct({...editingProduct, description: e.target.value})}
                  className="bg-slate-800/80 border-slate-600/80 text-white focus-visible:ring-[#2F80FF] min-h-[80px]"
                  rows={2}
                />
              </div>
              
              {/* Staff Prompt */}
              <div className="space-y-2">
                <Label className="text-slate-300">Bestellhilfe für Mitarbeiter</Label>
                <Textarea
                  data-testid="dialog-staff-prompt"
                  value={editingProduct.staff_prompt || ''}
                  onChange={(e) => setEditingProduct({...editingProduct, staff_prompt: e.target.value})}
                  placeholder="z.B. 'Welche Größe darf es sein?'"
                  className="bg-slate-800/80 border-slate-600/80 text-white placeholder:text-slate-500 focus-visible:ring-[#2F80FF] min-h-[80px]"
                  rows={2}
                />
              </div>
              
              {/* Variants */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300">Varianten</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingProduct({
                      ...editingProduct,
                      variants: [...(editingProduct.variants || []), { name: '', price: 0, is_default: false }]
                    })}
                    className="text-[#2DD4BF] hover:text-[#2DD4BF] hover:bg-[#2DD4BF]/10 text-xs h-7"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Hinzufügen
                  </Button>
                </div>
                <div className="space-y-2">
                  {editingProduct.variants?.map((variant, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input
                        value={variant.name}
                        onChange={(e) => {
                          const newVariants = [...editingProduct.variants];
                          newVariants[idx].name = e.target.value;
                          setEditingProduct({...editingProduct, variants: newVariants});
                        }}
                        placeholder="Name"
                        className="bg-slate-800/80 border-slate-600/80 text-white focus-visible:ring-[#2F80FF] flex-1"
                      />
                      <Input
                        type="number"
                        step="0.01"
                        value={variant.price}
                        onChange={(e) => {
                          const newVariants = [...editingProduct.variants];
                          newVariants[idx].price = parseFloat(e.target.value) || 0;
                          setEditingProduct({...editingProduct, variants: newVariants});
                        }}
                        placeholder="Preis"
                        className="bg-slate-800/80 border-slate-600/80 text-white focus-visible:ring-[#2F80FF] w-24"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newVariants = editingProduct.variants.filter((_, i) => i !== idx);
                          setEditingProduct({...editingProduct, variants: newVariants});
                        }}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Upsell Suggestions */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300">Upselling-Vorschläge</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingProduct({
                      ...editingProduct,
                      upsell_suggestions: [...(editingProduct.upsell_suggestions || []), '']
                    })}
                    className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 text-xs h-7"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Hinzufügen
                  </Button>
                </div>
                <div className="space-y-2">
                  {editingProduct.upsell_suggestions?.map((suggestion, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input
                        value={suggestion}
                        onChange={(e) => {
                          const newSuggestions = [...editingProduct.upsell_suggestions];
                          newSuggestions[idx] = e.target.value;
                          setEditingProduct({...editingProduct, upsell_suggestions: newSuggestions});
                        }}
                        placeholder="z.B. 'Pommes dazu? (+3,50€)'"
                        className="bg-slate-800/80 border-slate-600/80 text-white placeholder:text-slate-500 focus-visible:ring-[#2F80FF] flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newSuggestions = editingProduct.upsell_suggestions.filter((_, i) => i !== idx);
                          setEditingProduct({...editingProduct, upsell_suggestions: newSuggestions});
                        }}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              data-testid="dialog-cancel-button"
              variant="outline" 
              onClick={() => setEditModalOpen(false)} 
              className="border-slate-600 text-slate-300 hover:bg-slate-800/50"
            >
              Abbrechen
            </Button>
            <Button 
              data-testid="dialog-save-button"
              onClick={saveProductEdit} 
              className="bg-gradient-to-r from-[#2F80FF] to-[#2DD4BF] hover:from-[#4A92FF] hover:to-[#3ee4cf] text-white"
            >
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
